"use client";

import * as React from "react";

import { CaretSortIcon, CheckIcon } from "@radix-ui/react-icons";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Language,
  SupportedLanguages,
} from "@/components/language/supported-languages";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface LanguageComboboxProps {
  value?: string;
  isError?: boolean;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  type?: "native" | "toLearn";
  languagesAvailable?: Language[];
  isDisabled?: boolean;
}

export function LanguageCombobox({
  value,
  isError = false,
  onValueChange,
  type = "toLearn",
  languagesAvailable,
  isDisabled = false,
}: LanguageComboboxProps) {
  const { t } = useTranslation(["common"]);
  const [open, setOpen] = React.useState(false);
  const languages = React.useMemo(
    () => languagesAvailable ?? SupportedLanguages,
    [languagesAvailable],
  );

  const selectedLanguage = languages.find((language) => language.key === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={isDisabled}
          className={cn(
            "w-full justify-between font-normal truncate ",
            type === "native"
              ? "hover:border-blue-500"
              : "hover:border-primary",
            isError ? "border-destructive" : "",
            selectedLanguage ? "font-bold" : "text-muted-foreground",
          )}
        >
          {selectedLanguage
            ? t("common:languages." + selectedLanguage.key)
            : type === "native"
              ? t("common:languageComboBox.placeholderNative")
              : t("common:languageComboBox.placeholderToLearn")}
          <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start" side="bottom">
        <Command>
          <CommandInput placeholder={t("common:languageComboBox.search")} />
          <CommandList>
            <CommandEmpty>{t("common:languageComboBox.notFound")}</CommandEmpty>
            <CommandGroup>
              {languages.map((language) => (
                <CommandItem
                  key={language.key}
                  value={`${language.label}`}
                  onSelect={() => {
                    onValueChange?.(language.key);
                    setOpen(false);
                  }}
                >
                  <CheckIcon
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === language.key ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {t("common:languages." + language.key)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
